import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect } from 'react'

import { toAgentErrorMessage } from 'features/yieldbirdWrapperAgent/api/errors'
import {
  reportsQueryKey,
  reportStatusQueryOptions,
} from 'features/yieldbirdWrapperAgent/api/queries'
import {
  IN_PROGRESS_REPORT_STATUSES,
  PROCESSING_REPORT_STATUSES,
  STALLABLE_REPORT_STATUSES,
} from 'features/yieldbirdWrapperAgent/api/types'
import { useRestErrorSnackbar } from 'providers/tanstack-query-rest/useRestErrorSnackbar'
import { DEFAULT_REFETCH_INTERVAL } from 'providers/tanstack-query/constants'

import type {
  Report,
  ReportStatus,
  ReportStatusResponse,
} from 'features/yieldbirdWrapperAgent/api/types'

type UseTrackReport = {
  isProcessing: boolean
  isStalled: boolean
  isTracking: boolean
  reportStatus?: ReportStatusResponse
}

const STALLED_STATUS_AGE = 30 * 60 * 1_000 // [ms]; 30 minutes

const hasStatus = (statuses: readonly ReportStatus[], status: ReportStatus | undefined): boolean =>
  status !== undefined && statuses.includes(status)

const isStalledStatus = (report: ReportStatusResponse | undefined, fetchedAt: number): boolean =>
  report !== undefined &&
  hasStatus(STALLABLE_REPORT_STATUSES, report.status) &&
  fetchedAt - Date.parse(report.updatedAt) > STALLED_STATUS_AGE

export const useTrackReport = (jobId: Report['jobId'] | undefined): UseTrackReport => {
  const queryClient = useQueryClient()

  const { data, dataUpdatedAt, error } = useQuery({
    ...reportStatusQueryOptions(jobId),
    refetchInterval: ({ state }) =>
      state.status !== 'error' &&
      hasStatus(IN_PROGRESS_REPORT_STATUSES, state.data?.status) &&
      !isStalledStatus(state.data, state.dataUpdatedAt)
        ? DEFAULT_REFETCH_INTERVAL
        : false,
    retry: false,
  })

  useRestErrorSnackbar(error, toAgentErrorMessage)

  const status: ReportStatus | undefined = data?.status
  const isAwaitingStatus: boolean = status === undefined
  const isStalled: boolean = isStalledStatus(data, dataUpdatedAt)
  const canTrack: boolean = jobId !== undefined && error === null && !isStalled

  const isTracking: boolean =
    canTrack && (isAwaitingStatus || hasStatus(IN_PROGRESS_REPORT_STATUSES, status))

  const isProcessing: boolean =
    canTrack && (isAwaitingStatus || hasStatus(PROCESSING_REPORT_STATUSES, status))

  useEffect(() => {
    if (status !== undefined && status !== 'queued') {
      void queryClient.invalidateQueries({ queryKey: reportsQueryKey })
    }
  }, [status, queryClient])

  return { isProcessing, isStalled, isTracking, reportStatus: data }
}
